/* eslint-disable react/prop-types */
import React, {
  useEffect, useLayoutEffect, useRef, useState, useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import DraggableContent from './DraggableContent';

const POPOVER_WIDTH = 600;
const SCROLL_THRESHOLD = 10;
const ANCHOR_GAP = 8;
const VIEWPORT_PADDING = 8;

const PopoverContainer = styled('div')`
  position: fixed;
  width: ${POPOVER_WIDTH}px;
  /* Narrow viewports (e.g. laptop with sidebar open) can't comfortably
   * host 600px; drop to 500px below 1100px. computeInitialPosition reads
   * offsetWidth after paint so the flip/clamp logic stays in sync. */
  @media (max-width: 1100px) { width: 500px; }
  max-height: 80vh;
  overflow-y: auto;
  background: white;
  border: 1px solid #EEEEEE;
  border-radius: 6px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18), 0 0 1px rgba(0, 0, 0, 0.25);
  padding: 12px;
  /* Below rc-dropdown (1070), rc-trigger (1050), and ReactModal contents
   * (1100) so any menus/modals opened from inside the popover stack on
   * top instead of underneath. Above the calendar grid which uses no
   * z-index in the default flow. */
  z-index: 50;
`;

const Footer = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid #F0F0F0;
`;

const BackToDay = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #77A7FF;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  &:hover { opacity: 0.75; }
`;

const DateLabel = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #9E9E9E;
`;

/** Selectors for portal'd UI that may be opened from inside the
 * popover (rc-dropdown menus, ReactModal-based modals, react-tooltip,
 * react-select menus). Outside-click on these should NOT close the
 * popover. The [class*="__menu"] entry catches react-select's menu
 * portal/list/etc. regardless of classNamePrefix — options are always
 * descendants of the menu, so `closest()` finds the menu ancestor. */
const PORTAL_SELECTORS = [
  '.rc-dropdown',
  '.rc-trigger-popup',
  '.ReactModal__Overlay',
  '.ReactModal__Content',
  '.ReactModalPortal',
  '.react-tooltip',
  '[class*="__menu"]',
  /* 3-dot submenu popups (superset picker, pace picker) portal to body
   * with their own class names — not inside .rc-dropdown — so they
   * need explicit entries or clicking a color/pace closes the popover. */
  '.custom-submenu-supersetPicker',
  '.pace-dropdown',
  '.nested-submenu',
  /* Transposed add-exercise table's set-number superset picker also
   * portals to body with its own class — without this, choosing a
   * superset letter would close the popover. */
  '.transposed-superset-popover',
].join(',');

/**
 * Edit popover for a month-view exercise. Anchors to the clicked row
 * (Google Calendar style), repositioning on scroll/resize. Renders the
 * full week/day editor (`DraggableContent`) inside without the rbd
 * `Draggable` wrapper, so dragging stays scoped to the underlying
 * month-view item; the popover is for editing only.
 */
const MonthEditPopover = ({
  content,
  index,
  date,
  anchorEl,
  onClose,
  scrollContainerRef,
}) => {
  const popoverRef = useRef(null);
  const [position, setPosition] = useState(null);
  const [hasScrolledAway, setHasScrolledAway] = useState(false);
  const initialScrollRef = useRef(null);
  const dragIsInProgress = useSelector(
    (state) => state.calendar.data.dragIsInProgress,
  );

  /** Mirror DraggableDivWrapper's pattern: own localAdditionalInfo and
   * localDescription state here so the preSave tables can read and
   * write them. Pre-fill from content so database-populated values
   * show immediately when the popover opens. The popover is keyed by
   * assignedId in MonthView so it remounts (resetting these states to
   * the new item's values) when the user switches exercises. */
  const [localAdditionalInfo, setLocalAdditionalInfo] = useState(
    content?.additionalInfo || content?.exercise?.additionalInfo || '',
  );
  const [localDescription, setLocalDescription] = useState(
    content?.description || content?.exercise?.description || '',
  );

  const computeInitialPosition = useCallback(() => {
    if (!anchorEl) return null;
    const rect = anchorEl.getBoundingClientRect();
    /** detached or zero-sized — bail */
    if (rect.width === 0 && rect.height === 0) return null;

    const popoverEl = popoverRef.current;
    const measuredHeight = popoverEl
      ? Math.min(popoverEl.offsetHeight, window.innerHeight * 0.8)
      : 300;
    /** Read the actual rendered width so the media-query responsive size
     * (500px below 1100px, 600px above) is reflected in the flip/clamp. */
    const measuredWidth = popoverEl ? popoverEl.offsetWidth : POPOVER_WIDTH;

    /** Prefer right of anchor; flip to left if not enough room. */
    let left = rect.right + ANCHOR_GAP;
    if (left + measuredWidth > window.innerWidth - VIEWPORT_PADDING) {
      left = rect.left - measuredWidth - ANCHOR_GAP;
    }
    /** If neither side fits, clamp to viewport right edge. */
    if (left < VIEWPORT_PADDING) {
      left = Math.max(VIEWPORT_PADDING, window.innerWidth - measuredWidth - VIEWPORT_PADDING);
    }

    /** Vertically align top with anchor, clamp inside viewport. */
    let top = rect.top;
    if (top + measuredHeight > window.innerHeight - VIEWPORT_PADDING) {
      top = window.innerHeight - measuredHeight - VIEWPORT_PADDING;
    }
    if (top < VIEWPORT_PADDING) top = VIEWPORT_PADDING;

    return { top, left };
  }, [anchorEl]);

  /** Initial measurement after first paint so popover height is known.
   * Position is computed ONCE — the popover stays put after opening so
   * coaches can scroll the calendar and compare exercises without the
   * popover chasing the row off-screen. */
  useLayoutEffect(() => {
    setPosition(computeInitialPosition());
  }, [computeInitialPosition, content?.assignedId]);

  /** Re-clamp into the viewport on window resize so the popover
   * doesn't drift off-screen when the window shrinks. Does NOT
   * re-anchor — keeps the popover where it was opened. */
  useEffect(() => {
    const onResize = () => {
      setPosition((prev) => {
        if (!prev) return prev;
        const popoverEl = popoverRef.current;
        const measuredHeight = popoverEl
          ? Math.min(popoverEl.offsetHeight, window.innerHeight * 0.8)
          : 300;
        const measuredWidth = popoverEl ? popoverEl.offsetWidth : POPOVER_WIDTH;
        let { top, left } = prev;
        const maxLeft = window.innerWidth - measuredWidth - VIEWPORT_PADDING;
        const maxTop = window.innerHeight - measuredHeight - VIEWPORT_PADDING;
        if (left > maxLeft) left = Math.max(VIEWPORT_PADDING, maxLeft);
        if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
        if (top > maxTop) top = Math.max(VIEWPORT_PADDING, maxTop);
        if (top < VIEWPORT_PADDING) top = VIEWPORT_PADDING;
        if (top === prev.top && left === prev.left) return prev;
        return { top, left };
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /** When popover content grows (e.g. sets added), re-clamp the top
   * so the bottom edge stays inside the viewport. The popover only
   * moves UP — it never jumps back down once it has been pushed up. */
  useEffect(() => {
    const el = popoverRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      setPosition((prev) => {
        if (!prev) return prev;
        const maxTop = window.innerHeight - el.offsetHeight - VIEWPORT_PADDING;
        const clampedTop = Math.max(VIEWPORT_PADDING, maxTop);
        if (prev.top <= clampedTop) return prev;
        return { ...prev, top: clampedTop };
      });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /** Esc to close. */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /** Outside click. Clicks on the anchor row are ignored — the same-row
   * case is harmless (parent re-sets the same item) and clicking
   * another row re-anchors via parent state. Clicks on portal'd UI
   * (dropdowns, modals, react-select menus) the popover spawned are
   * also ignored so opening them doesn't dismiss the popover. */
  useEffect(() => {
    const onMouseDown = (e) => {
      if (!popoverRef.current) return;
      if (popoverRef.current.contains(e.target)) return;
      if (anchorEl && anchorEl.contains(e.target)) return;
      /** Skip if the click landed in a dropdown / modal / tooltip
       * portal'd above the popover — those are interactions the user
       * triggered FROM the popover and should not dismiss it. */
      if (e.target.closest && e.target.closest(PORTAL_SELECTORS)) return;
      onClose();
    };
    document.addEventListener('mousedown', onMouseDown, true);
    return () => document.removeEventListener('mousedown', onMouseDown, true);
  }, [anchorEl, onClose]);

  /** Close if a drag starts — the popover's anchor would otherwise
   * float along with the dragged ghost and editing during a drag
   * doesn't make sense. */
  useEffect(() => {
    if (dragIsInProgress) onClose();
  }, [dragIsInProgress, onClose]);

  /** Track whether the coach has scrolled the calendar away from the
   * position it was at when the popover opened, so we can show the
   * "Back to day" link when the anchor row is off-screen. */
  useEffect(() => {
    const el = scrollContainerRef?.current;
    if (!el) return undefined;
    initialScrollRef.current = { top: el.scrollTop, left: el.scrollLeft };
    const onScroll = () => {
      const { top, left } = initialScrollRef.current;
      const moved = Math.abs(el.scrollTop - top) > SCROLL_THRESHOLD
        || Math.abs(el.scrollLeft - left) > SCROLL_THRESHOLD;
      setHasScrolledAway(moved);
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [scrollContainerRef]);

  return createPortal(
    <PopoverContainer
      ref={popoverRef}
      role='dialog'
      aria-label={content?.title ? `Edit exercise: ${content.title}` : 'Edit exercise'}
      style={{
        top: position?.top ?? -9999,
        left: position?.left ?? -9999,
        visibility: position ? 'visible' : 'hidden',
      }}
    >
      <DraggableContent
        content={content}
        index={index}
        date={date}
        isHovered={false}
        isDragging={false}
        isExpanded
        setIsExpanded={() => {}}
        checkboxVisible={false}
        omitCheckbox
        isWeekView={false}
        shouldShowQuickActions
        measure={() => {}}
        onPopoverClose={onClose}
        localAdditionalInfo={localAdditionalInfo}
        setLocalAdditionalInfo={setLocalAdditionalInfo}
        localDescription={localDescription}
        setLocalDescription={setLocalDescription}
      />
      {date && (
        <Footer>
          {hasScrolledAway ? (
            <BackToDay
              onClick={() => {
                const el = scrollContainerRef?.current;
                if (!el || !initialScrollRef.current) return;
                el.scrollTo({
                  top: initialScrollRef.current.top,
                  left: initialScrollRef.current.left,
                  behavior: 'smooth',
                });
              }}
            >
              ← Back to day
            </BackToDay>
          ) : <span />}
          <DateLabel>{moment(date).format('ddd, MMM D, YYYY')}</DateLabel>
        </Footer>
      )}
    </PopoverContainer>,
    document.body,
  );
};

export default MonthEditPopover;
